import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import PaperCard from "./PaperCard";

// ── Agentic step labels shown in the chat UI ──────────────────────────────
const STEP_LABELS = {
  guardrail: { label: "Checking topic scope", icon: "⚖" },
  retrieve: { label: "Retrieving papers", icon: "🔍" },
  grade: { label: "Grading relevance", icon: "✦" },
  rewrite: { label: "Refining query", icon: "↺" },
  generate: { label: "Generating answer", icon: "✍" },
};

// ── Individual chat message ────────────────────────────────────────────────
function Message({ msg }) {
  const isUser = msg.role === "user";

  return (
    <motion.div
      className={`msg ${isUser ? "msg--user" : "msg--ai"}`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="msg__bubble">
        {msg.content}
        {msg.streaming && <span className="msg__cursor" />}
      </div>

      {/* Sources / paper citations */}
      {!isUser && msg.sources && msg.sources.length > 0 && (
        <div className="msg__sources">
          <p className="msg__sources-label">Sources</p>
          <div className="msg__sources-grid">
            {msg.sources.map((paper, i) => (
              <PaperCard key={paper.arxiv_id || i} paper={paper} index={i} />
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}

// ── Agentic reasoning steps panel ─────────────────────────────────────────
function ReasoningSteps({ steps }) {
  if (!steps || steps.length === 0) return null;
  return (
    <div className="reasoning">
      <p className="reasoning__title">Reasoning</p>
      <ul className="reasoning__list">
        {steps.map((step, i) => {
          const meta = STEP_LABELS[step.type] || {
            label: step.type,
            icon: "·",
          };
          return (
            <motion.li
              key={i}
              className={`reasoning__step reasoning__step--${step.status}`}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.06 }}
            >
              <span className="reasoning__icon">{meta.icon}</span>
              <span className="reasoning__label">{meta.label}</span>
              {step.score != null && (
                <span className="reasoning__score">
                  {(step.score * 100).toFixed(0)}%
                </span>
              )}
              {step.count != null && (
                <span className="reasoning__score">{step.count} docs</span>
              )}
            </motion.li>
          );
        })}
      </ul>
    </div>
  );
}

// ── Main ChatInterface component ──────────────────────────────────────────
export default function ChatInterface() {
  const [messages, setMessages] = useState([
    {
      role: "ai",
      content:
        "Welcome to Arcane Illumina. Ask me anything about AI and machine learning research — I will search arXiv papers to give you grounded, cited answers.",
      sources: [],
    },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [steps, setSteps] = useState([]);
  const [error, setError] = useState(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, steps]);

  async function sendMessage(e) {
    e?.preventDefault();
    const query = input.trim();
    if (!query || isLoading) return;

    setInput("");
    setError(null);
    setSteps([]);
    setIsLoading(true);

    // Add user message
    setMessages((prev) => [...prev, { role: "user", content: query }]);

    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Request failed" }));
        throw new Error(err.error || `HTTP ${res.status}`);
      }

      // Consume SSE stream
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let answer = "";

      // Place an empty AI bubble that we'll fill as tokens arrive
      setMessages((prev) => [
        ...prev,
        { role: "ai", content: "", streaming: true, sources: [] },
      ]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split("\n");
        buffer = lines.pop(); // keep incomplete last line

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          let event;
          try {
            event = JSON.parse(line.slice(6));
          } catch {
            continue;
          }

          if (event.type === "step") {
            setSteps((prev) => {
              // Update existing step or append new one
              const idx = prev.findIndex((s) => s.type === event.step);
              if (idx >= 0) {
                const next = [...prev];
                next[idx] = { ...next[idx], ...event };
                return next;
              }
              return [...prev, event];
            });
          } else if (event.type === "token") {
            answer += event.text;
            setMessages((prev) => {
              const last = prev[prev.length - 1];
              if (last?.role === "ai" && last.streaming) {
                return [...prev.slice(0, -1), { ...last, content: answer }];
              }
              return prev;
            });
          } else if (event.type === "done") {
            setMessages((prev) => {
              const last = prev[prev.length - 1];
              if (last?.role === "ai" && last.streaming) {
                return [
                  ...prev.slice(0, -1),
                  {
                    ...last,
                    content: answer || last.content,
                    streaming: false,
                    sources: event.sources || [],
                  },
                ];
              }
              return prev;
            });
            setSteps(event.steps || []);
          } else if (event.type === "error") {
            throw new Error(event.message);
          }
        }
      }
    } catch (err) {
      setError(err.message);
      // Remove the empty streaming bubble if it exists
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        return last?.streaming ? prev.slice(0, -1) : prev;
      });
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  }

  return (
    <div className="chat">
      {/* Message list */}
      <div className="chat__messages">
        {messages.map((msg, i) => (
          <Message key={i} msg={msg} />
        ))}

        {/* Live reasoning panel while streaming */}
        <AnimatePresence>
          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <ReasoningSteps steps={steps} />
            </motion.div>
          )}
        </AnimatePresence>

        {error && (
          <div className="chat__error">
            <span>⚠</span> {error}
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input bar */}
      <form className="chat__form" onSubmit={sendMessage}>
        <input
          ref={inputRef}
          className="input chat__input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about transformers, diffusion models, RAG, agents…"
          disabled={isLoading}
          autoFocus
        />
        <button
          type="submit"
          className="btn btn-primary chat__send"
          disabled={isLoading || !input.trim()}
        >
          {isLoading ? <span className="spinner" /> : "↑"}
        </button>
      </form>
    </div>
  );
}
